import { NodeLayout } from '@/components/nodes/layout';
import { Button } from '@/components/ui/button';
import { TextEditDialogEditor } from './text-edit-dialog-editor';
import {
  AIMessage,
  AIMessageContent,
} from '@/components/ui/kibo-ui/ai/message';
import { AIResponse } from '@/components/ui/kibo-ui/ai/response';
import {
  AISource,
  AISources,
  AISourcesContent,
  AISourcesTrigger,
} from '@/components/ui/kibo-ui/ai/source';
// shared UI
import { TwoRowToolbar, InstructionInput, NodeSurface, GenerateItem } from '@/components/nodes/shared';
import { ModelSelector } from '@/components/nodes/model-selector';
import { useSetReasoning } from '@/hooks/use-reasoning';
import { handleError } from '@/lib/error/handle';
import { textModels } from '@/lib/models/text';
import { getDefaultModelId, getLastSelectedModelId, getPresetModelId, rememberLastSelectedModelId } from '@flowmuse/models/shared/utils';
import {
  getDescriptionsFromImageNodes,
  getFilesFromFileNodes,
  getImagesFromImageNodes,
  getTextFromTextNodes,
  getTranscriptionFromAudioNodes,
  getTweetContentFromTweetNodes,
} from '@/lib/xyflow';
import { useProject } from '@/providers/project';
import { useEstimatedBilling } from '@/hooks/use-estimated-credits';
import { estimateTextUsdCost } from '@flowmuse/models/cost/text-estimate';
import { ReasoningTunnel } from '@/tunnels/reasoning';
import { useChat } from '@ai-sdk/react';
import { createTextHandleGenerate } from './handle-generate';
import { getIncomers, useReactFlow, useStore } from '@xyflow/react';
import { useAutoCancelHandler, useAutoGenerateHandler } from '@/components/canvas/utils/auto-generate-bus';
import type { JSONContent } from '@tiptap/react';
import { textToTiptapJSON } from '@/lib/utils';
import { Loader2Icon, Scissors as ScissorsIcon, Lock as LockIcon } from 'lucide-react';
import {
  type ChangeEventHandler,
  type ComponentProps,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import { mutate } from 'swr';
import type { TextNodeProps } from '.';
import { useI18n } from '@/providers/i18n';
import { selectNodeSelectedById } from '../store-selectors';
import { useUser } from '@/hooks/use-user';
import { TextCollapsedSummary } from '@/components/nodes/text/collapsed-summary';
import { usePreviewCanvas } from '@/hooks/use-preview-canvas';
import { filterModelsClient, useSubscription } from '@/providers/subscription';

import { TextTopToolbar } from './text-top-toolbar';
import { useTextSplit } from './use-text-split';

type TextTransformProps = TextNodeProps & {
  title: string;
};

export const TextTransform = memo(({
  data,
  id,
  type,
  title,
}: TextTransformProps) => {
  const { t } = useI18n();
  const { modelAccessMap, modelPassMap, userPlan, userInterval, userTags, planLevels } = useSubscription();
  const { updateNodeData, getNodes, getEdges, addNodes, addEdges, getNode } = useReactFlow();
  const { handleSplit: performSplit } = useTextSplit(id);
  const project = useProject();
  const { user } = useUser();
  const isPreview = usePreviewCanvas();
  const modelId =
    data.model ??
    getPresetModelId('text', textModels) ??
    getLastSelectedModelId('text', textModels) ??
    getDefaultModelId(textModels);
  // Phase 9.3: use set-only hook so TextTransform doesn't re-render when ANY
  // other node toggles the global reasoning panel state.
  const setReasoning = useSetReasoning();
  const successToastShownRef = useRef(false);

  const selectedModel = textModels[modelId];
  const estimatedUsdCost = useMemo(() => estimateTextUsdCost({ model: selectedModel }), [selectedModel]);
  const { credits: estimatedCredits, passRemaining, discountMultiplier, passCount, passDiscountMultiplier } = useEstimatedBilling(estimatedUsdCost, modelId);

  // 回填：节点初次创建且未设置 model 时，将选中的默认模型写回节点数据
  useEffect(() => {
    try {
      if (!data.model && modelId) {
        updateNodeData(id, { model: modelId });
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, modelId, data.model]);

  const { append, messages, setMessages, status, stop } = useChat({
    body: {
      modelId,
      projectId: project?.id,
    },
    onError: (error) => {
      handleError(t('Error generating text'), error);
      // Surface failure to the auto-run scheduler so downstream nodes can be
      // skipped immediately instead of waiting on the per-type timeout. The
      // text node doesn't otherwise set `status` (it streams via useChat),
      // so this is the only place the scheduler can learn about errors.
      try {
        updateNodeData(id, {
          status: 'failed',
          updatedAt: new Date().toISOString(),
          error: (error as any)?.message || 'Text generation failed',
        });
      } catch {}
    },
    onFinish: (message) => {
      updateNodeData(id, {
        generated: {
          text: message.content,
          sources:
            message.parts?.filter((part) => part.type === 'source') ?? [],
        },
        updatedAt: new Date().toISOString(),
      });

      setReasoning((oldReasoning) => ({
        ...oldReasoning,
        isGenerating: false,
      }));
      if (!successToastShownRef.current) {
        toast.success(t('Text generated successfully'));
        successToastShownRef.current = true;
      }

      const creditsKey = project?.id ? `credits:${project.id}` : 'credits';
      setTimeout(() => mutate(creditsKey), 5000);
    },
  });

  const handleClear = useCallback(() => {
    // 同时清空 generated.text, 根 text, content 和 useChat 的消息
    updateNodeData(id, { 
      generated: { ...(data.generated ?? {}), text: '' },
      content: textToTiptapJSON(''), 
      text: '',
      updatedAt: new Date().toISOString() 
    });
    setMessages([]);
    successToastShownRef.current = false;
  }, [id, data.generated, updateNodeData, setMessages]);

  const handlePaste = useCallback(async (clipHint?: string) => {
    if (isPreview) return;
    try {
      const clip = clipHint || (await navigator.clipboard.readText());
      if (!clip) return;
      const currentText = (messages.length
        ? messages
            .filter((message) => message.role === 'assistant')
            .map((message) => message.content)
            .join('\n')
        : data.generated?.text) ?? '';
      const nextText = currentText
        ? `${currentText}${currentText.endsWith('\n') ? '' : '\n'}${clip}`
        : clip;
      setEditContent(textToTiptapJSON(nextText));
      setIsEditDialogOpen(true);
    } catch {}
  }, [messages, data.generated?.text, isPreview]);

  const isGenerating = status === 'submitted' || status === 'streaming';
  // Instruction input refs and autoresize
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const adjustTextareaHeight = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.max(36, textareaRef.current.scrollHeight) + 'px';
    }
  }, []);
  useEffect(() => {
    adjustTextareaHeight();
  }, [data.instructions, adjustTextareaHeight]);


  // Dialog state for editing generated content
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editContent, setEditContent] = useState<JSONContent | null>(null);

  const handleSplitFromMenu = (mode: 'paragraph' | 'line' | 'sentence' | 'comma' = 'paragraph') => {
    const currentDisplayText = ((messages.length
      ? messages
          .filter((message) => message.role === 'assistant')
          .map((message) => message.content)
          .join('\n')
      : data.generated?.text) ?? '').trim();
    if (!currentDisplayText) {
      toast.error(t('No input provided'));
      return;
    }
    // Mirror to root primitive fields
    try {
      updateNodeData(id, {
        content: textToTiptapJSON(currentDisplayText) as any,
        text: currentDisplayText,
      });
    } catch {}

    performSplit(currentDisplayText, mode, {
      model: modelId,
      promptTemplate: (data as any)?.promptTemplate,
    });
  };

  // 自动运行：如果节点带有 autoRun 标记且尚未生成内容，则触发一次生成
  const autoRunTriggeredRef = useRef(false);
  useEffect(() => {
    if (autoRunTriggeredRef.current) return;
    if ((data as any)?.autoRun && !data.generated?.text && status !== 'submitted' && status !== 'streaming') {
      autoRunTriggeredRef.current = true;
      // 清掉 autoRun 标记，避免反复触发
      updateNodeData(id, { autoRun: false });
      // 延迟到下一个事件循环，确保连接关系已就绪
      setTimeout(() => {
        handleGenerate();
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerate = useCallback(
    createTextHandleGenerate({
      t,
      id,
      type,
      data,
      modelId,
      getNodes,
      getEdges,
      updateNodeData,
      append,
      setMessages,
    }),
    [t, id, type, data, modelId, getNodes, getEdges, updateNodeData, append, setMessages]
  );

  useAutoGenerateHandler(
    id,
    useCallback(() => {
      try {
        void handleGenerate();
      } catch {}
    }, [handleGenerate]),
  );

  // Canvas-wide "Stop" support — aborts the in-flight useChat stream and
  // surfaces the cancel as a `failed` status so the auto-run scheduler can
  // cascade-skip downstream nodes immediately.
  //
  // `dispatchAutoCancel()` broadcasts to EVERY registered handler (not just
  // the active nodes), so we MUST gate the failure write on `status` —
  // otherwise an idle LLM node sitting next to a running one would be
  // mistakenly marked as failed. The known race here (status lagging the
  // real useChat state by one render tick when append() was just called)
  // is bounded to ~1ms and is acceptable.
  useAutoCancelHandler(
    id,
    useCallback(() => {
      try {
        if (status === 'submitted' || status === 'streaming') {
          try { stop(); } catch {}
          try {
            updateNodeData(id, {
              status: 'failed',
              error: 'Cancelled by auto-run',
              updatedAt: new Date().toISOString(),
            });
          } catch {}
        }
      } catch {}
    }, [id, status, stop, updateNodeData]),
  );

  // Perf (Phase 9.5): stable identity for toolbar memo.
  const handleInstructionsChange = useCallback<ChangeEventHandler<HTMLTextAreaElement>>(
    (event) => updateNodeData(id, { instructions: event.target.value }),
    [id, updateNodeData],
  );

  const handleCopy = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(t('Copied to clipboard'));
  }, []);

  const toolbar = useMemo(() => {
    const controls: React.ReactNode[] = [];
    const withMeta = filterModelsClient(textModels, modelAccessMap, userPlan, userInterval, userTags, modelPassMap, planLevels);

    // Model selector (shared)
    controls.push(
      <ModelSelector
        key="model"
        id={id}
        value={modelId}
        options={withMeta}
        onChange={(value) => { rememberLastSelectedModelId('text', value); updateNodeData(id, { model: value }); }}
        disabled={isGenerating}
        width={200}
        className="rounded-xl h-9"
      />
    );

    // Generate / Regenerate (shared)
    controls.push(
      <GenerateItem
        key="generate"
        loading={isGenerating}
        generatedUrl={data.generated?.text ? '1' : undefined}
        onGenerate={() => { handleGenerate(); }}
        disabled={!project?.id || isGenerating}
        estimatedCredits={estimatedCredits}
        passRemaining={passRemaining}
        passCount={passCount}
        discountMultiplier={discountMultiplier}
        passDiscountMultiplier={passDiscountMultiplier}
      />
    );

    // No updated time indicator

    return [
      {
        children: (
          <TwoRowToolbar
            showInstructions={true}
            instructions={(
              <InstructionInput
                textareaRef={textareaRef}
                value={data.instructions ?? ''}
                onChange={handleInstructionsChange}
                onInput={adjustTextareaHeight}
                showPlaceholder={!(data.instructions ?? '').length}
                placeholder={t('Please provide generation instructions here...')}
              />
            )}
            controls={controls}
          />
        ),
      },
    ] as ComponentProps<typeof NodeLayout>['toolbar'];
    // Phase 12.2: removed `messages` from deps — toolbar body never reads it.
    // Previously, every streaming token bumped the messages array and rebuilt
    // the entire toolbar (incl. ModelSelector + InstructionInput + textarea
    // wrapper). isGenerating still drives loading/disabled UI and changes only
    // on status transitions (submit / streaming / ready), not per token.
    // Also removed `handleInstructionsChange` from deps — it's now stable
    // (Phase 9.5 useCallback).
  }, [
    id,
    modelId,
    data.instructions,
    data.generated?.text,
    project?.id,
    isGenerating,
    updateNodeData,
    handleGenerate,
    handleInstructionsChange,
    adjustTextareaHeight,
    estimatedCredits,
    passRemaining,
    discountMultiplier,
    t,
    modelAccessMap,
    modelPassMap,
    userPlan,
    userInterval,
    userTags,
    planLevels,
  ]);

  // 固定背景为白色（不再提供调色盘切换）
  const bgStyle = { backgroundColor: 'var(--node-bg)' } as const;

  const nonUserMessages = messages.filter((message) => message.role !== 'user');
  const isPreTokenLoading = status === 'submitted' || (status === 'streaming' && nonUserMessages.length === 0);

  const displayText = useMemo(() => {
    const fromMessages = messages.length
      ? messages
          .filter((message) => message.role === 'assistant')
          .map((message) => message.content)
          .join('\n')
      : undefined;
    return (fromMessages ?? data.generated?.text ?? '') as string;
  }, [messages, data.generated?.text]);

  const hasDisplayText = Boolean(String(displayText || '').trim().length);

  // 锁定状态：用于覆盖遮挡内容，防止看到提示词与文本
  const isLocked = (() => {
    try {
      const n = getNode(id) as any;
      return Boolean(n?.data?.locked ?? (data as any)?.locked);
    } catch {
      return Boolean((data as any)?.locked);
    }
  })();

  const isOriginalOwner = (() => {
    try {
      const originOwnerId = (project as any)?.originOwnerUserId ?? (project as any)?.userId;
      return Boolean(user?.id) && String(user?.id) === String(originOwnerId ?? '');
    } catch {
      return false;
    }
  })();

  // useEffect(() => {
  //   const hasReasoning = messages.some((message) =>
  //     message.parts.some((part) => part.type === 'reasoning')
  //   );

  //   if (hasReasoning && !reasoning.isReasoning && status === 'streaming') {
  //     setReasoning({ isReasoning: true, isGenerating: true });
  //   }
  // }, [messages, reasoning, status, setReasoning]);

  const selectIsSelected = useMemo(() => selectNodeSelectedById(id), [id]);
  const isSelected = useStore(selectIsSelected);
  const isCollapsed = Boolean((data as any)?.collapsed);

  const canOpenEditor = !isGenerating && (!isLocked || isOriginalOwner) && hasDisplayText;

  return (
    <NodeLayout
      id={id}
      data={data}
      title={title}
      type={type}
      variant="modern"
      collapsedAspectRatio="16/9"
      collapsedSummary={<TextCollapsedSummary data={data as any} />}
      contextMenuItems={[
        { label: t('Split'), onClick: handleSplitFromMenu, icon: <ScissorsIcon size={12} /> },
      ]}
      toolbar={toolbar}
    >
      {isSelected && !isCollapsed ? (
        <TextTopToolbar
          nodeId={id}
          text={displayText}
          onOpenEditor={() => {
            if (!hasDisplayText) return;
            setEditContent(textToTiptapJSON(displayText));
            setIsEditDialogOpen(true);
          }}
          onPaste={handlePaste}
          onSplit={handleSplitFromMenu}
          onClear={handleClear}
          isToolbarBlocked={isGenerating}
          showPrompt={false}
          canEdit={hasDisplayText}
        />
      ) : null}
      <NodeSurface
        aspectRatio={'16/4.5'}
        bgStyle={bgStyle}
        radiusClass="rounded-3xl"
        insetClass="inset-1"
        className=""
        showDashed={false}
      >
        <div
          className="relative flex h-full items-center justify-center p-6"
          onDoubleClick={() => {
            if (!canOpenEditor) return;
            setEditContent(textToTiptapJSON(displayText));
            setIsEditDialogOpen(true);
          }}
          title={canOpenEditor ? t('Double-click to edit') : undefined}
        >
          {isPreTokenLoading && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <LoaderWrapper />
            </div>
          )}

          {!isPreTokenLoading && !data.generated?.text && !nonUserMessages.length ? (
            <div
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
            >
              <div className="flex flex-col items-center gap-2 text-center">
                <p className="text-base font-medium text-foreground">{t('Ready to Create?')}</p>
                <p className="text-sm text-muted-foreground">{t('Click Generate to create text')}</p>
              </div>
            </div>
          ) : null}

          <div className="w-full">
            {isGenerating && nonUserMessages.length ? (
              nonUserMessages.map((message) => (
                <AIMessage
                  key={message.id}
                  from={message.role === 'assistant' ? 'assistant' : 'user'}
                  className="p-0 [&>div]:max-w-none"
                >
                  <div>
                    {message.parts.some((part) => part.type === 'source') && (
                      <AISources>
                        <AISourcesTrigger
                          count={message.parts.filter((part) => part.type === 'source').length}
                        />
                        <AISourcesContent>
                          {message.parts
                            .filter((part) => part.type === 'source')
                            .map(({ source }) => (
                              <AISource
                                key={source.url}
                                href={source.url}
                                title={source.title ?? new URL(source.url).hostname}
                              />
                            ))}
                        </AISourcesContent>
                      </AISources>
                    )}
                    <AIMessageContent className="bg-transparent p-0 break-words max-h-[100px] overflow-y-auto custom-scrollbar">
                      <AIResponse>{message.content}</AIResponse>
                    </AIMessageContent>
                  </div>
                </AIMessage>
              ))
            ) : data.generated?.text ? (
              <AIMessage
                from="assistant"
                className="p-0 [&>div]:max-w-none"
              >
                <div>
                  {Array.isArray(data.generated?.sources) && data.generated.sources.length > 0 && (
                    <AISources>
                      <AISourcesTrigger count={data.generated.sources.length} />
                      <AISourcesContent>
                        {data.generated.sources.map(({ source }: any) => (
                          <AISource
                            key={source.url}
                            href={source.url}
                            title={source.title ?? new URL(source.url).hostname}
                          />
                        ))}
                      </AISourcesContent>
                    </AISources>
                  )}
                  <AIMessageContent className="bg-transparent p-0 break-words max-h-[100px] overflow-y-auto custom-scrollbar">
                    <AIResponse>{data.generated.text}</AIResponse>
                  </AIMessageContent>
                </div>
              </AIMessage>
            ) : (
              Boolean(nonUserMessages.length) &&
              nonUserMessages.map((message) => (
                <AIMessage
                  key={message.id}
                  from={message.role === 'assistant' ? 'assistant' : 'user'}
                  className="p-0 [&>div]:max-w-none"
                >
                  <div>
                    {message.parts.some((part) => part.type === 'source') && (
                      <AISources>
                        <AISourcesTrigger
                          count={message.parts.filter((part) => part.type === 'source').length}
                        />
                        <AISourcesContent>
                          {message.parts
                            .filter((part) => part.type === 'source')
                            .map(({ source }) => (
                              <AISource
                                key={source.url}
                                href={source.url}
                                title={source.title ?? new URL(source.url).hostname}
                              />
                            ))}
                        </AISourcesContent>
                      </AISources>
                    )}
                    <AIMessageContent className="bg-transparent p-0 break-words max-h-[100px] overflow-y-auto custom-scrollbar">
                      <AIResponse>{message.content}</AIResponse>
                    </AIMessageContent>
                  </div>
                </AIMessage>
              ))
            )}
          </div>
        </div>

        {isLocked && !isOriginalOwner && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 backdrop-blur-sm">
            <div className="px-3 py-1.5 rounded-full border text-xs text-muted-foreground bg-card/80">
              {t('Locked')}
            </div>
          </div>
        )}
      </NodeSurface>

      <ReasoningTunnel.In>
        {messages.flatMap((message) =>
          message.parts
            .filter((part) => part.type === 'reasoning')
            .flatMap((part) =>
              part.details
                .filter((detail) => detail.type === 'text')
                .map((detail) => detail.text)
            )
        )}
      </ReasoningTunnel.In>

      <TextEditDialogEditor
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        title={t('Edit Text')}
        initialContent={editContent}
        readOnly={isPreview}
        onSave={({ text, json }) => {
          updateNodeData(id, {
            generated: {
              ...(data.generated ?? {}),
              text,
            },
            content: json as any,
            updatedAt: new Date().toISOString(),
          });
          setIsEditDialogOpen(false);
        }}
        onCancel={() => setIsEditDialogOpen(false)}
        placeholder={t('Start typing...')}
      />
    </NodeLayout>
  );
});
TextTransform.displayName = 'TextTransform';

const LoaderWrapper = () => (
  <div className="flex items-center justify-center">
    <Loader2Icon size={16} className="size-4 animate-spin text-muted-foreground" />
  </div>
);

// (no-op)
